<script setup>
import { onMounted, onUnmounted, ref, watch } from 'vue'
import ReportRepository from '../repository/ReportRepository.js'
import { Loader, TableSkeletonLoader, useToasterInSetup } from 'dmui_components'
import LiveReportList from './LiveReportList.vue'
import LiveReportFilters from './LiveReportFilters.vue'

defineProps({
	readonly: {
		type: Boolean,
		default: false
	}
})

const reports = ref()

const toaster = useToasterInSetup({ page: 'Live Reports' }, onUnmounted)

const DEFAULT_FILTERS = {
	document_type: [],
	document_year: [],
	status: [],
	country_name: [],
	industry_name: []
}

const DEFAULT_ORDER = {
	updated_at: -1
}

const DEFAULT_SEARCH = {
	document_title: '',
	org_name: ''
}

const DEFAULT_MODIFIERS = {
	search: { ...DEFAULT_SEARCH },
	filters: { ...DEFAULT_FILTERS },
	order: { ...DEFAULT_ORDER }
}

const modifiers = ref()

const loading = ref(false)

async function fetchReports() {
	loading.value = true
	try {
		reports.value = await ReportRepository.fetchReportsFromApi(modifiers.value)
	} catch (e) {
		console.error(e)
	} finally {
		loading.value = false
	}
}

async function onDeleteReport(reportId) {
	try {
		await ReportRepository.deleteReport(reportId)
		toaster.show('Report deleted successfully', { type: 'success' })
		await fetchReports()
	} catch (e) {
		console.error(e)
		toaster.show('Failed to delete report', { type: 'error' })
	}
}

async function onRetryReport(reportId, documentId, documentTitle) {
	try {
		await ReportRepository.retryReport(reportId, documentId, documentTitle)
		toaster.show('Processing report', { type: 'success' })
		await fetchReports()
	} catch (e) {
		console.error(e)
		toaster.show('Failed to retry report', { type: 'error' })
	}
}

watch(
	modifiers,
	async (newFilters) => {
		localStorage.setItem('live_report_modifiers', JSON.stringify(newFilters))
		await fetchReports()
	},
	{ deep: true }
)

onMounted(async () => {
	const savedFilters = localStorage.getItem('live_report_modifiers')
	if (savedFilters) {
		modifiers.value = JSON.parse(savedFilters)
	} else {
		modifiers.value = { ...DEFAULT_MODIFIERS }
	}
})
</script>

<template>
	<Loader v-if="!reports" />
	<div v-else>
		<LiveReportFilters v-model:filters="modifiers.filters" v-model:order="modifiers.order" :readonly="readonly" @reset:filters="modifiers.filters = { ...DEFAULT_FILTERS }" />
		<LiveReportList v-model:search="modifiers.search" :reports="reports" :readonly="readonly" @delete:report="onDeleteReport" @retry:report="onRetryReport">
			<TableSkeletonLoader v-if="loading" :n-rows="20" :n-columns="8" />
		</LiveReportList>
	</div>
</template>

<style scoped lang="less">
.column-order {
	gap: @micro;
}

:deep(.column-order-icon button) {
	display: none; // TODO: can be removed once the component is updated to be able to handle search independently
}
</style>
